import { readCommendaOSOrThrow } from '@commenda/api/commenda-os-dsl';
import type { CommendaOSFilter } from '@commenda/types';
import { LocationTypeRevamp, PersonLocationType } from '@commenda/types';
import type { DSLContext } from '@commenda/types/src/commenda-os-dsl';

import { DSLContextMarker } from '../dsl-constants';
import type { CommendaOSFieldMapping } from './commenda-os-extractors';

/** What a single Commenda OS read needs: the entity, the field path and any relation filters. */
interface CommendaOSReadTarget {
  entity: string;
  readPath: string;
  filters?: CommendaOSFilter[];
}

interface PersonLocation {
  type?: string[];
  address?: unknown;
}

/** Reads form fields' current values from Commenda OS, outside of any one form's state. */
export class CommendaOSReadHelper {
  private static readonly CONTEXT_MARKERS = new Set<string>([
    DSLContextMarker.FORM_CONTEXT,
    DSLContextMarker.ENTITY_CONTEXT,
  ]);

  /**
   * The DSL path for one field. Filters go on the relation (the first path segment); a read path
   * that already carries its own `[...]` keeps those and ignores `filters`.
   *
   * @example
   * buildReadPath('corporation', 'governmentIdentifiers.value', [{ key: 'identifierTypeId', value: 'GID:US:EIN' }])
   * // → 'corporation.governmentIdentifiers[identifierTypeId=GID:US:EIN].value'
   */
  static buildReadPath(
    entity: string,
    readPath: string,
    filters?: readonly CommendaOSFilter[]
  ): string {
    const readPathHasFilters = readPath.includes('[') && readPath.includes(']');
    if (!filters || filters.length === 0 || readPathHasFilters) {
      return `${entity}.${readPath}`;
    }
    const filterStr = filters.map((f) => `${f.key}=${f.value}`).join(',');
    const parts = readPath.split('.');
    if (parts.length > 1) {
      return `${entity}.${parts[0]}[${filterStr}].${parts.slice(1).join('.')}`;
    }
    return `${entity}.${readPath}[${filterStr}]`;
  }

  /**
   * One field's value, or null when there is none. Throws when the read fails, so a failure is
   * never mistaken for an empty field. A key person's locations are read as the
   * full list and narrowed to the filtered location type here, because the list has no filter.
   */
  static async readValue(
    target: Readonly<CommendaOSReadTarget>,
    context: DSLContext,
    shareToken: string | undefined
  ): Promise<unknown> {
    const isPersonLocations =
      target.entity === 'keyPerson' && target.readPath === 'locations';
    const fullPath = CommendaOSReadHelper.buildReadPath(
      target.entity,
      target.readPath,
      isPersonLocations ? undefined : target.filters
    );
    const value = await readCommendaOSOrThrow(fullPath, context, shareToken);
    if (value === null || value === undefined) return null;
    if (!isPersonLocations || !Array.isArray(value)) return value;

    const locationTypeFilter = target.filters?.find(
      (f) => f.key === 'locationType'
    )?.value;
    if (!locationTypeFilter) return value;
    const typeMatch =
      locationTypeFilter === LocationTypeRevamp.MAILING_ADDRESS
        ? PersonLocationType.MAILING
        : locationTypeFilter === LocationTypeRevamp.PRINCIPAL_PLACE_OF_BUSINESS
          ? PersonLocationType.OPERATING
          : null;
    // The DSL returns a key person's locations as this shape.
    const matched = typeMatch
      ? (value as PersonLocation[]).find((loc) =>
          loc.type?.some((t) => t?.includes(typeMatch))
        )
      : null;
    return {
      locationType: locationTypeFilter,
      address: matched?.address ?? {},
    };
  }

  /**
   * The saved values of every readable field, keyed by fieldId — what a drawer opened on this
   * record would be prefilled with. A field bound to another answer in the form is skipped:
   * outside the form there is no answer to bind to, and the drawer cannot read it either.
   * Throws when any read fails.
   */
  static async readFormValues(
    mappings: readonly CommendaOSFieldMapping[],
    context: DSLContext,
    shareToken: string | undefined
  ): Promise<Record<string, unknown>> {
    const values: Record<string, unknown> = {};
    await Promise.all(
      mappings.map(async (mapping) => {
        if (!mapping.readPath) return;
        if (!CommendaOSReadHelper.canReadOutsideForm(mapping, context)) return;
        const value = await CommendaOSReadHelper.readValue(
          {
            entity: mapping.entity,
            readPath: mapping.readPath,
            filters: mapping.filters,
          },
          context,
          shareToken
        );
        if (value !== null) values[mapping.fieldId] = value;
      })
    );
    return values;
  }

  private static canReadOutsideForm(
    mapping: Readonly<CommendaOSFieldMapping>,
    context: DSLContext
  ): boolean {
    return (mapping.contextBindings ?? []).every(
      (binding) =>
        CommendaOSReadHelper.CONTEXT_MARKERS.has(binding.sourceFieldId) &&
        context[binding.contextKey] !== undefined &&
        context[binding.contextKey] !== null
    );
  }
}
